import React from 'react';
import { Link } from 'react-router-dom';
import { Car, ArrowLeft } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-900 hover:text-red-600 transition-colors">
            <Car className="w-8 h-8 text-red-600" />
            <span className="text-xl font-bold">AutoFlow</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <article className="bg-white rounded-xl shadow-sm p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Política de Privacidad</h1>
          <p className="text-slate-500 mb-8 pb-8 border-b">
            <strong>AutoFlow</strong> | Última actualización: abril 2025
          </p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Quiénes somos</h2>
              <p className="text-slate-600 leading-relaxed">
                AutoFlow es una plataforma SaaS de gestión comercial para agencias de automóviles. Proveemos herramientas digitales incluyendo asistentes de WhatsApp con inteligencia artificial para cada agencia suscripta.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Datos que recopilamos</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                Al interactuar con los asistentes de WhatsApp de las agencias que usan AutoFlow, podemos recopilar:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                <li>Número de teléfono de WhatsApp</li>
                <li>Nombre de perfil de WhatsApp</li>
                <li>Contenido de los mensajes enviados al bot</li>
                <li>Fecha y hora de las interacciones</li>
                <li>Información de vehículos consultados</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-3">
                Cada agencia es responsable de los datos de sus propios clientes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">3. Cómo usamos los datos</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                Los datos recopilados se utilizan exclusivamente para:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                <li>Responder consultas comerciales de los usuarios</li>
                <li>Mejorar la experiencia de atención al cliente</li>
                <li>Registrar el historial de conversaciones para continuidad del servicio</li>
                <li>Generar estadísticas de uso para las agencias</li>
              </ul>
              <p className="text-slate-700 font-medium mt-4 p-3 bg-slate-50 rounded-lg">
                No vendemos ni compartimos datos personales con terceros.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">4. WhatsApp Business API</h2>
              <p className="text-slate-600 leading-relaxed">
                Los asistentes de AutoFlow operan mediante la API oficial de WhatsApp Business de Meta. Al interactuar con cualquier bot de una agencia AutoFlow, aceptás los{' '}
                <a href="https://www.whatsapp.com/legal/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">
                  Términos de Servicio de WhatsApp
                </a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Almacenamiento y seguridad</h2>
              <p className="text-slate-600 leading-relaxed">
                Los datos se almacenan en servidores seguros con acceso restringido. Aplicamos medidas técnicas y organizativas para proteger la información contra acceso no autorizado.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">6. Retención de datos</h2>
              <p className="text-slate-600 leading-relaxed">
                Los datos de conversación se conservan por un período máximo de 12 meses, salvo que la agencia o el usuario solicite su eliminación antes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">7. Derechos del usuario</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                Podés solicitar en cualquier momento:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                <li>Acceso a tus datos</li>
                <li>Corrección de datos incorrectos</li>
                <li>Eliminación de tus datos</li>
                <li>Restricción del procesamiento</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-3">
                Para ejercer estos derechos, contactate con la agencia con la que interactuaste, o escribinos a:{' '}
                <a href="mailto:privacidad@autoflowapp.net" className="text-red-600 hover:underline font-medium">
                  privacidad@autoflowapp.net
                </a>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">8. Cookies</h2>
              <p className="text-slate-600 leading-relaxed">
                El sitio web de AutoFlow puede utilizar cookies técnicas necesarias para el funcionamiento de la plataforma. No usamos cookies de seguimiento publicitario.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">9. Cambios en esta política</h2>
              <p className="text-slate-600 leading-relaxed">
                Nos reservamos el derecho de actualizar esta política. Notificaremos cambios significativos publicando la nueva versión en esta página.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">10. Contacto</h2>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-slate-700 font-medium">AutoFlow</p>
                <p className="text-slate-600">autoflowapp.net</p>
                <p className="text-slate-600">
                  Email:{' '}
                  <a href="mailto:privacidad@autoflowapp.net" className="text-red-600 hover:underline">
                    privacidad@autoflowapp.net
                  </a>
                </p>
              </div>
            </section>
          </div>
        </article>

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <Link to="/terms" className="hover:text-red-600 transition-colors">
            Términos y Condiciones
          </Link>
          <span className="mx-2">•</span>
          <Link to="/privacy" className="hover:text-red-600 transition-colors font-medium text-slate-700">
            Política de Privacidad
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Car className="w-5 h-5 text-red-500" />
            <span className="font-semibold">AutoFlow</span>
          </div>
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} AutoFlow. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
